import React, { useState } from 'react';
import { FiMail, FiAlertCircle, FiLoader, FiDownload, FiUpload, FiEye, FiEyeOff } from 'react-icons/fi';
import AppModal from '../../common/AppModal';
import services from '../../services';
import { toast } from 'react-toastify';

const DEFAULT_FORM = {
    name: '',
    email: '',
    password: '',
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    imapSecure: true,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 465,
    smtpSecure: true,
};

const ImapAuthUser = ({ imapStatus, onClose, onConnected }) => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const resetAndClose = () => {
        setStep(1);
        setEmail('');
        setFormData(DEFAULT_FORM);
        setErrorMessage('');
        setShowPassword(false);
        onClose();
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleDetect = async (e) => {
        e.preventDefault();
        if (!email) return;
        setFormData({ ...DEFAULT_FORM, email });
        setStep(2);
    };

    const handleConnect = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setIsSubmitting(true);
        try {
            await services.imapEmail.CONNECT({
                ...formData,
                imapPort: Number(formData.imapPort),
                smtpPort: Number(formData.smtpPort),
            });
            toast.success('Mailbox connected successfully!');
            onConnected && onConnected();
            resetAndClose();
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to connect mailbox';
            toast.error(message);
            setErrorMessage(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footerStep1 = (
        <>
            <button type="button" className="imap-btn-secondary" onClick={resetAndClose}>
                Close
            </button>
            <button type="submit" form="imap-detect-form" className="imap-btn-primary" disabled={!email}>
                Next
            </button>
        </>
    );

    const footerStep2 = (
        <>
            <button type="button" className="imap-btn-secondary" onClick={() => setStep(1)}>
                Back
            </button>
            <button type="submit" form="imap-connect-form" className="imap-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <FiLoader className="spin-icon" size={14} /> : null}
                {isSubmitting ? ' Connecting...' : 'Connect Mailbox'}
            </button>
        </>
    );

    return (
        <>
            {step === 1 && (
                <AppModal
                    show={imapStatus}
                    onClose={resetAndClose}
                    icon={<FiMail size={18} color="#fff" />}
                    title="Connect Webmail"
                    subtitle="Enter your email to detect IMAP/SMTP settings"
                    maxWidth="480px"
                    footer={footerStep1}
                >
                    <form id="imap-detect-form" onSubmit={handleDetect}>
                        <div className="cred-form-group">
                            <label htmlFor="detect-email" className="cred-form-label">Email Address</label>
                            <input
                                id="detect-email"
                                type="email"
                                className="cred-form-input"
                                placeholder="you@yourcompany.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoFocus
                            />
                        </div>
                    </form>
                </AppModal>
            )}

            {step === 2 && (
                <AppModal
                    show={imapStatus}
                    onClose={resetAndClose}
                    icon={<FiMail size={18} color="#fff" />}
                    title="Connect Webmail"
                    subtitle="Enter your IMAP/SMTP server details"
                    maxWidth="620px"
                    footer={footerStep2}
                >
                    {errorMessage && (
                        <div className="cred-alert-error">
                            <FiAlertCircle size={15} />
                            {errorMessage}
                        </div>
                    )}

                    <form id="imap-connect-form" onSubmit={handleConnect}>
                        <div className="cred-form-grid">
                            <div className="cred-form-group">
                                <label htmlFor="name" className="cred-form-label">Mailbox Name</label>
                                <input
                                    id="name"
                                    type="text"
                                    name="name"
                                    className="cred-form-input"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Support Mailbox"
                                    required
                                />
                            </div>
                            <div className="cred-form-group">
                                <label htmlFor="email" className="cred-form-label">Email Address</label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    className="cred-form-input"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="support@yourcompany.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="cred-form-group">
                            <label htmlFor="password" className="cred-form-label">Password</label>
                            <div className="imap-password-field">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    className="cred-form-input"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="Mailbox password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="imap-password-toggle"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </button>
                            </div>
                            <small className="imap-field-hint">
                                Using Gmail? Use an App Password instead of your regular password.{' '}
                                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
                                    Get your App Password here
                                </a>
                            </small>
                        </div>

                        <div className="imap-section-title"><FiDownload size={12} /> Incoming (IMAP)</div>
                        <div className="cred-form-grid">
                            <div className="cred-form-group">
                                <label htmlFor="imapHost" className="cred-form-label">IMAP Host</label>
                                <input
                                    id="imapHost"
                                    type="text"
                                    name="imapHost"
                                    className="cred-form-input"
                                    value={formData.imapHost}
                                    onChange={handleInputChange}
                                    placeholder="mail.yourcompany.com"
                                    required
                                />
                            </div>
                            <div className="cred-form-group">
                                <label htmlFor="imapPort" className="cred-form-label">IMAP Port</label>
                                <input
                                    id="imapPort"
                                    type="number"
                                    name="imapPort"
                                    className="cred-form-input"
                                    value={formData.imapPort}
                                    onChange={handleInputChange}
                                    placeholder="993"
                                    required
                                />
                            </div>
                        </div>
                        <div className="cred-form-group">
                            <label className="imap-checkbox-row">
                                <input
                                    type="checkbox"
                                    name="imapSecure"
                                    checked={formData.imapSecure}
                                    onChange={handleInputChange}
                                />
                                <span>Use SSL/TLS for IMAP</span>
                            </label>
                        </div>

                        <div className="imap-section-title"><FiUpload size={12} /> Outgoing (SMTP)</div>
                        <div className="cred-form-grid">
                            <div className="cred-form-group">
                                <label htmlFor="smtpHost" className="cred-form-label">SMTP Host</label>
                                <input
                                    id="smtpHost"
                                    type="text"
                                    name="smtpHost"
                                    className="cred-form-input"
                                    value={formData.smtpHost}
                                    onChange={handleInputChange}
                                    placeholder="mail.yourcompany.com"
                                    required
                                />
                            </div>
                            <div className="cred-form-group">
                                <label htmlFor="smtpPort" className="cred-form-label">SMTP Port</label>
                                <input
                                    id="smtpPort"
                                    type="number"
                                    name="smtpPort"
                                    className="cred-form-input"
                                    value={formData.smtpPort}
                                    onChange={handleInputChange}
                                    placeholder="465"
                                    required
                                />
                            </div>
                        </div>
                        <div className="cred-form-group">
                            <label className="imap-checkbox-row">
                                <input
                                    type="checkbox"
                                    name="smtpSecure"
                                    checked={formData.smtpSecure}
                                    onChange={handleInputChange}
                                />
                                <span>Use SSL/TLS for SMTP</span>
                            </label>
                        </div>
                    </form>
                </AppModal>
            )}
        </>
    );
};

export default ImapAuthUser;
